import styled, { css } from 'styled-components';
import { colors, r4, s8 } from '@ardoq/design-tokens';
import { UnstyledButton } from '@ardoq/button';
import { IconSize } from '@ardoq/icons';
import { text2Bold } from '@ardoq/typography';
import {
  InputStyleProps,
  PRESET_ICON_TOP,
  commonInputStyle,
} from '../../atoms';

const FIT_TEXT_MIN_WIDTH = '12ch';

export const TextInputStyle = styled.input<
  InputStyleProps & {
    $hasPrefix?: boolean;
    $isDisabled?: boolean;
    $fitsText?: boolean;
  }
>`
  background-color: ${({ $isDisabled }) =>
    $isDisabled ? colors.surfaceDisabled : colors.surfaceDefault};
  color: ${({ $isDisabled }) =>
    $isDisabled ? colors.textDisabled : colors.textDefault};
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      &::placeholder {
        color: ${colors.textDisabled};
      }
    `}

  &:read-only:not(:disabled) {
    background-color: ${colors.surfaceStatusNeutral};
    color: ${colors.textDefault};
  }

  position: relative;
  ${commonInputStyle};
  text-align: ${({ $alignText }) => $alignText};
  border-radius: ${({ $hasPrefix, $hasPostfix }) =>
    `${$hasPrefix ? 0 : r4} ${$hasPostfix ? 0 : r4} ${$hasPostfix ? 0 : r4} ${
      $hasPrefix ? 0 : r4
    }`};
  ${({ $fitsText }) =>
    $fitsText &&
    css`
      field-sizing: content;
      min-width: ${FIT_TEXT_MIN_WIDTH};
    `}
`;

// WCAG 2.5.8 minimum pointer target. The icon inside stays 20px, so the button
// is inset by half the difference and the glyph lands where PresetIcon puts it.
const ICON_BUTTON_SIZE = 24;
const ICON_BUTTON_INSET = (ICON_BUTTON_SIZE - IconSize.MEDIUM) / 2;

// UnstyledButton brings the element reset and the focus ring; it also sets
// position: relative, which the absolute positioning here overrides.
export const IconButtonInInput = styled(UnstyledButton)`
  position: absolute;
  top: ${PRESET_ICON_TOP - ICON_BUTTON_INSET}px;
  right: calc(${s8} - ${ICON_BUTTON_INSET}px);
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${ICON_BUTTON_SIZE}px;
  height: ${ICON_BUTTON_SIZE}px;
  border-radius: ${r4};
  color: ${colors.iconAction};
  /* InputWrapper turns pointer events off for read-only fields too, and a
     read-only field can still carry an action such as copy. */
  pointer-events: auto;

  &:hover:not([aria-disabled='true']) {
    color: ${colors.iconActionHover};
  }

  &[aria-disabled='true'] {
    color: ${colors.iconActionDisabled};
    cursor: default;
  }
`;

const Msg = styled.div<{ $isDisabled?: boolean }>`
  ${text2Bold};
  white-space: nowrap;
  background-color: ${({ $isDisabled }) =>
    $isDisabled ? colors.surfaceDisabled : colors.surfaceDefaultSubtle};
  padding: 7px;
  color: ${({ $isDisabled }) =>
    $isDisabled ? colors.textDisabled : colors.textDefault};
  box-shadow: inset 0px 0px 0px 1px ${colors.borderAction};
`;

export const PrefixMsg = styled(Msg)`
  border-radius: ${r4} 0 0 ${r4};
`;

export const PostfixMsg = styled(Msg)`
  border-radius: 0 ${r4} ${r4} 0;
`;
